import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  AppBar,
  Toolbar,
  IconButton,
  Card,
  CardContent,
  Avatar,
  Divider,
  Button,
  Collapse,
  Tabs,
  Tab,
  TextField
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useNavigate, useLocation } from "react-router-dom";
import FormsBottomNavbar from "../Post Module/FormsBottomNavbar";
import { tabs, filterProperties, utilityFunctions } from "./propertyFilters";

const PropertyResults = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get filters from navigation state
  const initialFilters = location.state?.filters || {};
  
  // Initialize state with filter values from navigation
  const [searchQuery, setSearchQuery] = useState(initialFilters.searchQuery || "");
  const [selectedLocations, setSelectedLocations] = useState(initialFilters.selectedLocations || []);
  const [selectedPropertyTypes, setSelectedPropertyTypes] = useState(initialFilters.selectedPropertyTypes || []);
  const [useCurrentLocation, setUseCurrentLocation] = useState(initialFilters.useCurrentLocation || false);
  const [activeTab, setActiveTab] = useState(initialFilters.activeTab || 0);
  
  // Existing state variables
  const [activeCommentIndex, setActiveCommentIndex] = useState(null);
  const [commentText, setCommentText] = useState("");
  const [propertyLink, setPropertyLink] = useState("");
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [responses, setResponses] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [expandedResponses, setExpandedResponses] = useState({});

  const colors = {
    primary: "#1a1a1a",
    secondary: "#8B7355",
    accent: "#A52A2A",
    background: "#b9a990",
    cardBackground: "#FFFFFF",
    textDark: "#1a1a1a",
    textLight: "#FFFFFF",
    textMuted: "#666666"
  };

  // Property link validation patterns
  const getValidLinkPatterns = () => {
    const basePatterns = {
      Purchase: [/^https?:\/\/landnest\.net\/buy-description\/\d+$/, /^https?:\/\/localhost:3000\/buy-description\/\d+$/],
      Rent: [/^https?:\/\/landnest\.net\/rent-description\/\d+$/, /^https?:\/\/localhost:3000\/rent-description\/\d+$/],
      Lease: [/^https?:\/\/landnest\.net\/lease-description\/\d+$/, /^https?:\/\/localhost:3000\/lease-description\/\d+$/]
    };
    return basePatterns[tabs[activeTab]?.label] || [/^https?:\/\/landnest\.net\/.+\/\d+$/, /^https?:\/\/localhost:3000\/.+\/\d+$/];
  };

  const isValidPropertyLink = (link) => {
    const patterns = getValidLinkPatterns();
    return patterns.some(pattern => pattern.test(link));
  };

  // Format dimensions function
  const formatDimensions = (property) => {
    if (property.length > 0 && property.width > 0) {
      const area = property.length * property.width;
      const units = property.units || 'ft';
      return `${property.length}*${property.width} (${area}) ${units === 'm' ? 'sq m' : 'sq ft'}`;
    }
    return null;
  };

  // Function to navigate to all responses page
  const handleViewAllResponses = (property) => {
    navigate('/all-responses', {
      state: {
        responses: responses[property.req_id] || [],
        propertyDetails: property
      }
    });
  };

  // Function to navigate back to search filters
  const handleModifySearch = () => {
    navigate('/property-search', {
      state: {
        filters: {
          searchQuery,
          selectedLocations,
          selectedPropertyTypes,
          useCurrentLocation,
          activeTab
        }
      }
    });
  };

  const handleTabChange = (event, newValue) => setActiveTab(newValue);

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        setLoading(true);
        const response = await fetch("https://landnest.net:81/property-request/");
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();
        setProperties(data.slice().reverse());
        setError(null);
      } catch (err) {
        console.error("Error fetching properties:", err);
        setError("Failed to load properties. Please try again later.");
      } finally {
        setLoading(false);
      }
    };
    fetchProperties();
  }, []);

  useEffect(() => {
    const fetchResponses = async () => {
      try {
        const response = await fetch("https://landnest.net:81/response-property-request/");
        if (response.ok) {
          const data = await response.json();
          const responsesByReqId = {};
          data.forEach(response => {
            if (!responsesByReqId[response.req_id]) responsesByReqId[response.req_id] = [];
            responsesByReqId[response.req_id].push(response);
          });
          setResponses(responsesByReqId);
        }
      } catch (err) {
        console.error("Error fetching responses:", err);
      }
    };
    fetchResponses();
  }, []);

  const { formatBudget, formatDate, getUserInitials } = utilityFunctions;

  const handleCommentClick = (index) => {
    setActiveCommentIndex(activeCommentIndex === index ? null : index);
    setCommentText("");
    setPropertyLink("");
  };

  const handleSubmitResponse = async (reqId, userId) => {
    if (!commentText.trim() && !propertyLink.trim()) {
      alert("Please enter either a comment or property link");
      return;
    }

    if (propertyLink.trim() && !isValidPropertyLink(propertyLink.trim())) {
      const currentTab = tabs[activeTab]?.label;
      const validExamples = {
        Purchase: "https://landnest.net/buy-description/162 or http://localhost:3000/buy-description/59",
        Rent: "https://landnest.net/rent-description/162 or http://localhost:3000/rent-description/88",
        Lease: "https://landnest.net/lease-description/162 or http://localhost:3000/lease-description/99"
      };
      alert(`Invalid property link for ${currentTab} section.\n\nValid examples:\n${validExamples[currentTab] || "Please use proper landnest.net links"}`);
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch("https://landnest.net:81/response-property-request/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ comment: commentText, proerty_link: propertyLink, req_id: reqId, user_id: userId }),
      });

      if (response.ok) {
        const newResponse = await response.json();
        setResponses(prev => ({ ...prev, [reqId]: [...(prev[reqId] || []), newResponse] }));
        setCommentText("");
        setPropertyLink("");
        setActiveCommentIndex(null);
        alert("Response submitted successfully!");
      } else throw new Error("Failed to submit response");
    } catch (err) {
      console.error("Error submitting response:", err);
      alert("Failed to submit response. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const toggleResponses = (reqId) => {
    setExpandedResponses(prev => ({ ...prev, [reqId]: !prev[reqId] }));
  };

  // Apply filters to properties
  const filteredProperties = filterProperties(properties, { 
    searchQuery, 
    activeTab, 
    selectedLocations, 
    selectedPropertyTypes, 
    tabs 
  });

  if (loading) return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", backgroundColor: colors.background, justifyContent: "center", alignItems: "center" }}>
      <Typography variant="h6" color={colors.textDark}>Loading properties...</Typography>
    </Box>
  );

  if (error) return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", backgroundColor: colors.background, justifyContent: "center", alignItems: "center" }}>
      <Typography variant="h6" color="error">{error}</Typography>
    </Box>
  );

  const renderResponseItem = (response, index) => (
    <Box key={index} sx={{ mb: 1, p: 1, backgroundColor: 'white', borderRadius: 0.5, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', border: '1px solid #e0e0e0' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
        <Avatar sx={{ width: 20, height: 20, mr: 0.75, bgcolor: colors.secondary, fontSize: '0.6rem', color: colors.textLight }}>U</Avatar>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <Typography variant="caption" fontWeight="bold" color={colors.primary} sx={{ fontSize: '0.65rem' }}>User {response.user_id}</Typography>
          <Typography variant="caption" color={colors.textMuted} sx={{ fontSize: '0.6rem' }}>{formatDate(response.created_at || new Date())}</Typography>
        </Box>
      </Box>
      {response.comment && <Typography variant="body2" sx={{ color: colors.textDark, lineHeight: 1.2, fontSize: '0.7rem' }}>Click on below link to view the property.</Typography>}
      {response.proerty_link && (
        <Box sx={{ mt: 0.5 }}>
          <Typography component="a" href={response.proerty_link} target="_blank" rel="noopener noreferrer"
            sx={{ color: '#1976d2', textDecoration: 'underline', fontSize: '0.65rem', '&:hover': { color: '#1565c0', textDecoration: 'underline' } }}>
            🔗 Property Link
          </Typography>
        </Box>
      )}
    </Box>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", backgroundColor: colors.background }}>
      <AppBar position="sticky" sx={{ backgroundColor: colors.primary, color: colors.textLight }}>
        <Toolbar>
          <IconButton onClick={() => navigate(-1)} sx={{ color: colors.textLight }}>
            <ArrowBackIosNewIcon />
          </IconButton>
          <Typography variant="h6" sx={{ ml: 2, flex: 1 }}>Property Results</Typography>
          <Button 
            onClick={handleModifySearch}
            sx={{ 
              color: colors.textLight,
              textTransform: 'none',
              fontSize: '0.875rem'
            }}
          >
            Modify Search
          </Button>
        </Toolbar>
      </AppBar>

      {/* Tabs Section - Only tabs displayed */}
      <Box sx={{ width: '100%', bgcolor: colors.cardBackground, borderBottom: 1, borderColor: 'divider' }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange} 
          variant="scrollable" 
          scrollButtons="auto"
          sx={{ 
            '& .MuiTab-root': { 
              fontSize: '0.8rem', 
              fontWeight: 'bold', 
              color: colors.textMuted, 
              minWidth: 'auto', 
              px: 2, 
              '&.Mui-selected': { color: colors.secondary } 
            }, 
            '& .MuiTabs-indicator': { backgroundColor: colors.secondary } 
          }}
        >
          {tabs.map((tab, index) => (
            <Tab key={index} label={tab.label} />
          ))}
        </Tabs>
      </Box>

      {/* Properties Results Section - Only results displayed */}
      <Box sx={{ p: 1.5, flex: 1, pb: 10, backgroundColor: colors.background }}>
        {filteredProperties.length === 0 ? (
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Typography variant="h6" sx={{ mb: 2, color: colors.textDark }}>
              No properties found for {tabs[activeTab]?.label}
            </Typography>
            <Button 
              variant="contained" 
              onClick={handleModifySearch}
              sx={{ 
                backgroundColor: colors.secondary, 
                color: colors.textLight,
                '&:hover': { backgroundColor: colors.accent }
              }}
            >
              Modify Search Criteria
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {filteredProperties.map((property, index) => (
              <Card 
                key={property.req_id} 
                sx={{ 
                  borderRadius: 2, 
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)', 
                  border: `1px solid #e0e0e0`, 
                  overflow: 'hidden' 
                }}
              >
                <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                  {/* Updated header section with Purchase and Budget in separate boxes */}
                  <Box sx={{ p: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: colors.primary }}>
                    {/* Purchase Section - Black Background */}
                    <Box sx={{ 
                      backgroundColor: "#b9a990", 
                      color: colors.primary,
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 3,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '0.9rem', lineHeight: 1 }}>
                        {property.looking_for ? property.looking_for.charAt(0).toUpperCase() + property.looking_for.slice(1) : "Property"}
                      </Typography>
                    </Box>
                    
                    {/* Budget Section - Brown Background */}
                    <Box sx={{ 
                      backgroundColor: colors.cardBackground, 
                      color: colors.primary,
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 3,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Typography variant="subtitle1" fontWeight="bold" sx={{ lineHeight: 1, fontSize: '0.9rem' }}>
                        Budget: {formatBudget(property.min_budget, property.max_budget)}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ p: 1.5 }}>
                    <Box sx={{ display: 'grid', gap: 0.75 }}>
                      {/* Type */}
                      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                        <Typography variant="body2" color={colors.textDark} sx={{ fontSize: '0.75rem', lineHeight: 1.2, minWidth: 90, flexShrink: 0 }}>
                          <strong>Property Type:</strong>
                        </Typography>
                        <Typography variant="body2" color={colors.textDark} sx={{ fontSize: '0.75rem', lineHeight: 1.2 }}>
                          {property.property_type || "Not specified"}
                        </Typography>
                      </Box>

                      {/* Location */}
                      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                        <Typography variant="body2" color={colors.textDark} sx={{ fontSize: '0.75rem', lineHeight: 1.2, minWidth: 90, flexShrink: 0 }}>
                          <strong>Location:</strong>
                        </Typography>
                        <Typography variant="body2" color={colors.textDark} sx={{ fontSize: '0.75rem', lineHeight: 1.2 }}>
                          {property.location || "Not specified"}
                        </Typography>
                      </Box>

                      {/* Dimensions when available */}
                      {formatDimensions(property) && (
                        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                          <Typography variant="body2" color={colors.textDark} sx={{ fontSize: '0.75rem', lineHeight: 1.2, minWidth: 90, flexShrink: 0 }}>
                            <strong>Length*Width:</strong>
                          </Typography>
                          <Typography variant="body2" color={colors.textDark} sx={{ fontSize: '0.75rem', lineHeight: 1.2 }}>
                            {formatDimensions(property)}
                          </Typography>
                        </Box>
                      )}
                      
                      {/* Fallback to area if dimensions not available */}
                      {(property.area > 0 && !formatDimensions(property)) && (
                        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                          <Typography variant="body2" color={colors.textDark} sx={{ fontSize: '0.75rem', lineHeight: 1.2, minWidth: 90, flexShrink: 0 }}>
                            <strong>Length*Width:</strong>
                          </Typography>
                          <Typography variant="body2" color={colors.textDark} sx={{ fontSize: '0.75rem', lineHeight: 1.2 }}>
                            {`${property.area} ${property.units || 'sq ft'}`}
                          </Typography>
                        </Box>
                      )}
                      
                      {/* Fallback to acres if neither dimensions nor area available */}
                      {((property.min_acres > 0 || property.max_acres > 0) && !formatDimensions(property) && property.area <= 0) && (
                        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                          <Typography variant="body2" color={colors.textDark} sx={{ fontSize: '0.75rem', lineHeight: 1.2, minWidth: 90, flexShrink: 0 }}>
                            <strong>Length*Width:</strong>
                          </Typography>
                          <Typography variant="body2" color={colors.textDark} sx={{ fontSize: '0.75rem', lineHeight: 1.2 }}>
                            {`${property.min_acres}${property.max_acres > property.min_acres ? `-${property.max_acres}` : ''} acres`}
                          </Typography>
                        </Box>
                      )}
                      
                      {/* Ratio */}
                      {property.ratio && (
                        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                          <Typography variant="body2" color={colors.textDark} sx={{ fontSize: '0.75rem', lineHeight: 1.2, minWidth: 80, flexShrink: 0 }}>
                            <strong>Ratio:</strong>
                          </Typography>
                          <Typography variant="body2" color={colors.textDark} sx={{ fontSize: '0.75rem', lineHeight: 1.2 }}>
                            {property.ratio}
                          </Typography>
                        </Box>
                      )}

                      {/* User and Date as key-value pairs */}
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', mt: 0.5 }}>
                        <Avatar sx={{ width: 16, height: 16, fontSize: '0.5rem' }}>
                          👤
                        </Avatar>
                        <Typography variant="body2" color={colors.textDark} sx={{ fontSize: '0.75rem', lineHeight: 1.2, minWidth: 75, flexShrink: 0 }}>
                          <strong>CUST {property.user_id}</strong>
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="body2" color={colors.textDark} sx={{ fontSize: '0.75rem', lineHeight: 1.2 }}>
                            {formatDate(property.created_at)}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    {/* Responses and Comment Button - Right aligned */}
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between', 
                      gap: 1, 
                      mt: 1, 
                      pt: 1, 
                      borderTop: '1px solid #e0e0e0' 
                    }}>
                      {/* Left side - Toggle Responses Button */}
                      <Button 
                        size="small" 
                        onClick={() => toggleResponses(property.req_id)} 
                        sx={{ 
                          color: colors.secondary, 
                          fontSize: '0.65rem', 
                          minWidth: 'auto', 
                          px: 0.5, 
                          textTransform: 'none',
                          fontWeight: 'bold'
                        }}
                        endIcon={expandedResponses[property.req_id] ? 
                          <ExpandLessIcon sx={{ fontSize: '0.8rem' }} /> : 
                          <ExpandMoreIcon sx={{ fontSize: '0.8rem' }} />
                        }
                      >
                        {expandedResponses[property.req_id] ? 'Hide' : 'Show'} Responses
                      </Button>
                      
                      {/* Right side - Responses count and Comment Button */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <ChatBubbleOutlineIcon sx={{ fontSize: '0.8rem', color: colors.secondary }} />
                        <Typography variant="body2" sx={{ fontSize: '0.7rem', color: colors.secondary, fontWeight: 'bold' }}>
                          {responses[property.req_id]?.length || 0}
                        </Typography>
                        <IconButton 
                          size="small" 
                          onClick={() => handleCommentClick(index)} 
                          sx={{ 
                            color: colors.textLight, 
                            backgroundColor: colors.secondary, 
                            '&:hover': { backgroundColor: colors.accent }, 
                            width: 24, 
                            height: 24 
                          }}
                        >
                          <ChatBubbleOutlineIcon sx={{ fontSize: '0.8rem' }} />
                        </IconButton>
                      </Box>
                    </Box>

                    {/* User comment */}
                    {property.comment && property.comment !== "Property request from form" && property.comment.length < 100 && (
                      <Box sx={{ mt: 1, p: 1, backgroundColor: '#f5f2ee', borderRadius: 0.5, borderLeft: `2px solid ${colors.secondary}` }}>
                        <Typography variant="body2" color={colors.textDark} fontStyle="italic" sx={{ fontSize: '0.7rem', lineHeight: 1.2 }}>
                          "{property.comment.length > 80 ? property.comment.substring(0, 80) + '...' : property.comment}"
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {/* Expanded Responses */}
                  {responses[property.req_id]?.length > 0 && (
                    <Collapse in={expandedResponses[property.req_id]}>
                      <Box sx={{ p: 1, backgroundColor: '#fafafa' }}>
                        {/* Show only 1 response initially */}
                        {responses[property.req_id].slice(0, 1).map((response, responseIndex) => renderResponseItem(response, responseIndex))}
                        
                        {/* Show "View All" button if there are more responses */}
                        {responses[property.req_id].length > 1 && (
                          <Box sx={{ textAlign: 'center', mt: 1 }}>
                            <Button 
                              size="small" 
                              onClick={() => handleViewAllResponses(property)}
                              sx={{ 
                                color: colors.secondary, 
                                fontSize: '0.65rem', 
                                textTransform: 'none',
                                fontWeight: 'bold'
                              }}
                            >
                              + {responses[property.req_id].length - 1} more responses
                            </Button>
                          </Box>
                        )}
                      </Box>
                    </Collapse>
                  )}

                  {/* Comment Input Section */}
                  {activeCommentIndex === index && (
                    <Box sx={{ p: 1, backgroundColor: '#f5f2ee', borderTop: `1px solid ${colors.secondary}` }}>
                      <TextField 
                        fullWidth 
                        variant="outlined" 
                        placeholder="Paste property link (landnest.net or localhost:3000)" 
                        value={propertyLink} 
                        onChange={(e) => setPropertyLink(e.target.value)} 
                        size="small" 
                        sx={{ mb: 1 }}
                        InputProps={{ sx: { fontSize: '0.75rem', '& input': { py: 0.75 } } }} 
                      />
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                        <Button 
                          size="small" 
                          onClick={() => handleCommentClick(index)} 
                          sx={{ color: colors.secondary, fontSize: '0.65rem', minWidth: 'auto', px: 1 }}
                        >
                          Cancel
                        </Button>
                        <Button 
                          variant="contained" 
                          size="small" 
                          onClick={() => handleSubmitResponse(property.req_id, 1)} 
                          disabled={submitting || (!commentText.trim() && !propertyLink.trim())}
                          sx={{ 
                            backgroundColor: colors.secondary, 
                            color: colors.textLight, 
                            fontSize: '0.65rem', 
                            minWidth: 'auto', 
                            px: 1, 
                            '&:hover': { backgroundColor: colors.accent } 
                          }}
                        >
                          {submitting ? "..." : "Submit"}
                        </Button>
                      </Box>
                    </Box>
                  )}
                </CardContent>
              </Card>
            ))}
          </Box>
        )}
      </Box>

      <Box sx={{ position: "fixed", bottom: 0, left: 0, right: 0 }}>
        <FormsBottomNavbar />
      </Box>
    </Box>
  );
};

export default PropertyResults;